import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import FeedbackWidget from './FeedbackWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Context for feedback state
const FeedbackContext = createContext({
  pendingSurveys: [],
  triggeredSurveys: [],
  hasPending: false,
  showSurvey: () => {},
  dismissSurvey: () => {},
  refreshSurveys: () => {}
});

export const useFeedback = () => useContext(FeedbackContext);

// Provider component
export function FeedbackProvider({ children }) {
  const [pendingSurveys, setPendingSurveys] = useState([]);
  const [triggeredSurveys, setTriggeredSurveys] = useState([]);
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [dismissedSurveys, setDismissedSurveys] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('dismissedSurveys') || '[]');
    } catch {
      return [];
    }
  });
  const [lastCheck, setLastCheck] = useState(0);

  // Fetch pending surveys
  const refreshSurveys = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/feedback/user/pending-surveys`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        
        // Filter out dismissed surveys
        const filteredSurveys = (data.surveys || []).filter(
          s => !dismissedSurveys.includes(s.id)
        );
        const filteredTriggered = (data.triggered_surveys || []).filter(
          s => !dismissedSurveys.includes(s.survey_template_id)
        );
        
        setPendingSurveys(filteredSurveys);
        setTriggeredSurveys(filteredTriggered);
        
        // Auto-show triggered survey if exists and none active
        if (filteredTriggered.length > 0 && !activeSurvey) {
          const triggered = filteredTriggered[0];
          // Only show if last check was more than 5 minutes ago
          const now = Date.now();
          if (now - lastCheck > 5 * 60 * 1000) {
            setActiveSurvey(triggered.survey_template_id);
            setLastCheck(now);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch pending surveys:', error);
    }
  }, [dismissedSurveys, activeSurvey, lastCheck]);

  // Check for surveys on mount and periodically
  useEffect(() => {
    // Initial check after 3 seconds
    const initialTimeout = setTimeout(refreshSurveys, 3000);
    
    // Periodic check every 5 minutes
    const interval = setInterval(refreshSurveys, 5 * 60 * 1000);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [refreshSurveys]);

  // Show a specific survey
  const showSurvey = (surveyId) => {
    setActiveSurvey(surveyId);
  };

  // Dismiss a survey
  const dismissSurvey = (surveyId) => {
    const newDismissed = [...dismissedSurveys, surveyId];
    setDismissedSurveys(newDismissed);
    localStorage.setItem('dismissedSurveys', JSON.stringify(newDismissed));
    setActiveSurvey(null);
  };

  // Handle survey completion
  const handleComplete = () => {
    if (activeSurvey) {
      dismissSurvey(activeSurvey);
    }
    refreshSurveys();
  };

  // Handle survey close
  const handleClose = () => {
    if (activeSurvey) {
      dismissSurvey(activeSurvey);
    }
  };

  const value = {
    pendingSurveys,
    triggeredSurveys,
    hasPending: pendingSurveys.length > 0 || triggeredSurveys.length > 0,
    showSurvey,
    dismissSurvey,
    refreshSurveys
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      
      {/* Active survey widget */}
      {activeSurvey && (
        <FeedbackWidget
          surveyId={activeSurvey}
          position="bottom-right"
          trigger="auto"
          onComplete={handleComplete}
          onClose={handleClose}
        />
      )}
    </FeedbackContext.Provider>
  );
}

// Feedback bell/badge component for navbar
export function FeedbackBadge({ className }) {
  const { hasPending, pendingSurveys, showSurvey } = useFeedback();

  if (!hasPending) return null;

  return (
    <button
      onClick={() => pendingSurveys.length > 0 && showSurvey(pendingSurveys[0].id)}
      className={`relative p-2 rounded-lg hover:bg-gray-100 transition-colors ${className}`}
      title="You have pending feedback surveys"
      data-testid="feedback-badge"
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        className="text-gray-600"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        <path d="M13 8H7"/>
        <path d="M17 12H7"/>
      </svg>
      <span className="absolute -top-1 -right-1 h-4 w-4 bg-violet-500 text-white text-xs rounded-full flex items-center justify-center">
        {pendingSurveys.length}
      </span>
    </button>
  );
}

export default FeedbackProvider;
